import { NgModule } from '@angular/core';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FlexLayoutModule } from '@angular/flex-layout';
import { TranslateModule } from '@ngx-translate/core';
import { RouterModule } from '@angular/router';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { TextFieldModule } from '@angular/cdk/text-field';
import { NgxDropzoneModule } from 'ngx-dropzone';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgxSpinnerModule } from 'ngx-spinner';
import { ImageCropperModule } from 'ngx-image-cropper';
import { ColorPickerModule } from 'ngx-color-picker';

import { TopoLogoComponent } from './components/topo-logo/topo-logo.component';
import { TopoTableComponent } from './components/topo-table/topo-table.component';
import { TopoTableColumnComponent } from './components/topo-table/topo-table-column.component';
import { TopoTableColumnsComponent } from './components/topo-table/topo-table-columns.component';
import { TopoTablePagerComponent } from './components/topo-table/topo-table-pager.component';
import { ActionMenuComponent } from './components/action-menu/action-menu.component';
import { ActionComponent } from './components/action-menu/action.component';
import { ColorDisplayComponent } from './components/color-display/color-display.component';
import { ModalComponent } from './components/modal/modal.component';
import { ConfirmDeleteComponent } from './components/confirm-delete/confirm-delete.component';

import { RoutingEffects } from './store/routing/routing.effects';
import { InfiniteScrollerComponent } from './components/infinite-scroller/infinite-scroller.component';
import { TextColumnComponent } from './components/columns/text-column/text-column.component';
import { DynamicColumnDirective } from './components/columns/dynamic-column.directive';
import { DynamicFieldDirective } from './components/fields/dynamic-field.directive';
import { DynamicFieldDirective2 } from './components/fields/dynamic-field.directive2';
import { DateTimeColumnComponent } from './components/columns/date-time-column/date-time-column.component';
import { MultiPicklistColumnComponent } from './components/columns/multi-picklist-column/multi-picklist-column.component';
import { NumberColumnComponent } from './components/columns/number-column/number-column.component';
import { PicklistColumnComponent } from './components/columns/picklist-column/picklist-column.component';
import { EnumerationColumnComponent } from './components/columns/enumeration-column/enumeration-column.component';
import { UrlColumnComponent } from './components/columns/url-column/url-column.component';
import { UidColumnComponent } from './components/columns/uid-column/uid-column.component';
import { UserColumnComponent } from './components/columns/user-column/user-column.component';
import { GeometryColumnComponent } from './components/columns/geometry-column/geometry-column.component';
import { ImageColumnComponent } from './components/columns/image-column/image-column.component';
import { EmailColumnComponent } from './components/columns/email-column/email-column.component';
import { PhoneColumnComponent } from './components/columns/phone-column/phone-column.component';
import { ReferenceColumnComponent } from './components/columns/reference-column/reference-column.component';
import { AutoIncrementColumnComponent } from './components/columns/auto-increment-column/auto-increment-column.component';
import { UserFieldComponent } from './components/fields/user-field/user-field.component';
import { ColorPickerComponent } from './components/color-picker/color-picker.component';
import { VersionCheckComponent } from './components/version-check/version-check.component';
import { ColorItemSelectComponent } from './components/color-item-select/color-item-select.component';
import { RightDrawerComponent } from './components/right-drawer/right-drawer.component';
import { IconItemSelectComponent } from './components/icon-item-select/icon-item-select.component';
import { TextFieldComponent } from './components/fields/text-field/text-field.component';
import { DateFieldComponent } from './components/fields/date-field/date-field.component';
import { MatListModule } from '@angular/material/list';
import { MatTreeModule } from '@angular/material/tree';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSliderModule } from '@angular/material/slider';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';
import { GeometryFieldComponent } from './components/fields/geometry-field/geometry-field.component';
import { ImageFieldComponent } from './components/fields/image-field/image-field.component';
import { PicklistFieldComponent } from './components/fields/picklist-field/picklist-field.component';
import { MultiPicklistFieldComponent } from './components/fields/multi-picklist-field/multi-picklist-field.component';
import { UidFieldComponent } from './components/fields/uid-field/uid-field.component';
import { EmailFieldComponent } from './components/fields/email-field/email-field.component';
import { PhoneFieldComponent } from './components/fields/phone-field/phone-field.component';
import { NumberFieldComponent } from './components/fields/number-field/number-field.component';
import { UrlFieldComponent } from './components/fields/url-field/url-field.component';
import { AutoIncrementFieldComponent } from './components/fields/auto-increment-field/auto-increment-field.component';
import { ReferenceFieldComponent } from './components/fields/reference-field/reference-field.component';

import { TableColumnSelection } from './components/ag-grid/select-columns/select-columns.component';

import { DocumentSummaryComponent } from './components/fields/document-summary/document-summary.component';
import { WorkflowTransitionSelectComponent2 } from './components/workflow-transition-select2/workflow-transition-select2.component';
import { WorkflowSelectComponent } from './components/fields/workflow-select/workflow-select.component';
import { AvatarComponent } from './components/avatar/avatar.component';
import { FileUploadComponent } from './components/file-upload/file-upload.component';
import { SpinnerComponent } from './components/spinner/spinner.component';
import { ProfileMenuComponent } from './components/profile-menu/profile-menu.component';
import { ConfirmComponent } from './components/confirm/confirm.component';
import * as store from './store';
import { UserProfileEffects } from './store/user-profile';
import { UserProfileComponent } from './components/user-profile/user-profile.component';
import { AppMenuComponent } from './components/app-menu/app-menu.component';
import { ColorFieldComponent } from './components/fields/color-field/color-field.component';
import { BooleanFieldComponent } from './components/fields/boolean-field/boolean-field.component';
import { SwitchComponent } from './components/switch/switch.component';
import { TabsComponent } from './components/tabs/tabs.component';
import { RecipientSelectComponent } from './components/recipient-select/recipient-select.component';
import { ContactSelectComponent } from './components/contact-select/contact-select.component';
import { WizardDialogComponent } from './pages/wizard-dialog/wizard-dialog.component';
import { WorkflowTransitionFormComponent } from './pages/workflow-transition/workflow-transition-form.component';
import { RelatedDocumentsComponent } from './components/related-documents/related-documents-view/related-documents.component';
import { RelatedDocumentCardComponent } from './components/related-documents/related-document-card/related-document-card.component';
import { DateTimePickerComponent } from './components/date-time-picker/date-time-picker.component';
import { TagPillComponent } from './components/tag-pill/tag-pill.component';

import {
  DateTimeCellComponent,
  GeometryCellComponent,
  ImageCellComponent,
  TextCellComponent,
  UserCellComponent,
  NumberCellComponent,
  UrlCellComponent,
  PicklistCellComponent,
  ReferenceCellComponent,
  MultiPicklistCellComponent,
  WorkflowCellComponent,
  BooleanCellComponent,
  ColorCellComponent,
  DistanceCellComponent,
  EmailCellComponent,
  PhoneCellComponent,
  CultureCodeCellComponent,
  DurationCellComponent,
  EnumerationCellComponent,
  BusinessUnitsCellComponent
} from './components/ag-grid';
import { CopyClipboardDirective } from './components/copyClipboard/copy-to-clipboard.directive';

import { AgGridModule } from 'ag-grid-angular';
import { AllEnterpriseModule, LicenseManager, ModuleRegistry } from 'ag-grid-enterprise';
ModuleRegistry.registerModules([AllEnterpriseModule]);
LicenseManager.setLicenseKey(
  'Using_this_{AG_Grid}_Enterprise_key_{AG-096602}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Topo.ai}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{Topo_ONE}_only_for_{2}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_working_on_{Topo_ONE}_need_to_be_licensed___{Topo_ONE}_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{7_March_2027}____[v3]_[01]_MTgwNDM3NzYwMDAwMA==5399d3e5e978cef3eeac4fadaf57c3d0'
);

import { DistanceFieldComponent } from './components/fields/distance-field/distance-field.component';
import { ToolTipComponent } from './components/tool-tip/tool-tip.component';
import { VideoPlayerComponent } from './components/video-player/video-player.component';
import { HeaderBarTabButtonComponent } from './components/header-bar-tab-button/header-bar-tab-button.component';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { DocumentFilterDialogComponent } from './components/filters/document-filter-dialog/document-filter-dialog.component';
import { FilterItemComponent } from './components/filters/filter-item/filter-item.component';
import { FilterGroupComponent } from './components/filters/filter-group/filter-group.component';
import { FilterDisplayComponent } from './components/filters/filter-display/filter-display.component';
import { TemplateBuilderComponent } from './components/template-builder/template-builder.component';
import { RichTextEditComponent } from './components/rich-text-edit/rich-text-edit.component';
import { FieldSelectModalComponent } from './components/filters/field-select-modal/field-select-modal.component';
import { LinkCellComponent } from './components/ag-grid/link-cell/link-cell.component';
import { ActionCellComponent } from './components/ag-grid/action-cell/action-cell.component';
import { ColorCountPillComponent } from './components/color-count-pill/color-count-pill.component';
import { TooltipComponent } from './components/tooltip/tooltip.component';
import { TooltipDirective } from './components/tooltip/tooltip.directive';
import { VerticalTabsComponent } from './components/vertical-tabs/vertical-tabs.component';
import { SliderComponent } from './components/slider/slider.component';
import { FileUploadNewComponent } from './components/file-upload/file-upload-new.component';
import { WorkflowStepDisplayComponent } from './components/workflow-step-display/workflow-step-display.component';
import { DataTableComponent } from './components/data-table/data-table.component';
import { DataTableServerComponent } from './components/data-table-server/data-table.component';
import { EnumTranslateCellComponent } from './components/ag-grid/enum-translate-cell/enum-translate-cell.component';
import { ArrayFieldComponent } from './components/fields/array-field/array-field.component';
import { ArrayColumnComponent } from './components/columns/array-column/array-column.component';
import { ArrayCellComponent } from './components/ag-grid/document-fields/array-cell/array-cell.component';
import { VideoWindowComponent } from './components/video-window/video-window.component';
import { QuillModule } from 'ngx-quill';
import { OpenSourceLicensesComponent } from './components/open-source-licenses/open-source-licenses.component';
import { CultureCodeColumnComponent } from './components/columns/culture-code-column/culture-code-column.component';
import { CultureCodeFieldComponent } from './components/fields/culture-code-field/culture-code-field.component';
import { TextInputAutocompleteDirective } from './components/text-input-autocomplete/text-input-autocomplete.directive';
import { TextInputAutocompleteContainerComponent } from './components/text-input-autocomplete/text-input-autocomplete-container.component';
import { TextInputAutocompleteMenuComponent } from './components/text-input-autocomplete/text-input-autocomplete-menu.component';
import { TableFilterComponent } from './components/table-filter/table-filter.component';
import { RecipientInlineSelectComponent } from './communications/recipients/recipient-inline-select/recipient-inline-select.component';
import { RecipientListSelectComponent } from './communications/recipients/recipient-list-select/recipient-list-select.component';
import { RecipientsSelectionComponent } from './communications/recipients/recipient-selection/recipients-selection.component';
import { RecipientDetailsListComponent } from './communications/recipients/recipient-details-list/recipient-details-list.component';
import { DocumentLinkFilterDialogComponent } from './components/link-filters/document-link-filter-dialog/document-filter-dialog.component';
import { LinkFilterItemComponent } from './components/link-filters/filter-item/filter-item.component';
import { LinkFilterGroupComponent } from './components/link-filters/filter-group/filter-group.component';
import { LinkFilterDisplayComponent } from './components/link-filters/link-filter-display/filter-display.component';
import { LinkFieldSelectModalComponent } from './components/link-filters/field-select-modal/field-select-modal.component';
import { LinkFilterDatasetItemComponent } from './components/link-filters/filter-dataset-item/filter-dataset-item.component';
import { NotificationLogComponent } from './communications/notification-log/notification-log.component';
import { NotificationLogStatusDetailComponent } from './communications/notification-log/status-detail/status-detail.component';
import { FormGroupSelectComponent } from './components/form-group-select/form-group-select.component';
import { RelatedDocumentsFilterComponent } from './components/related-documents/related-documents-filter/related-documents-filter.component';
import { RelatedIncidentCardComponent } from './components/related-documents/card-controls/incident-card/incident-card.component';
import { RelatedAssetFacilityCardComponent } from './components/related-documents/card-controls/asset-facility-card/asset-facility-card.component';
import { RelatedAssetPersonCardComponent } from './components/related-documents/card-controls/asset-person-card/asset-person-card.component';
import { RecipientsFromTemplateComponent } from './communications/recipients/recipients-from-template/recipients-from-template.component';
import { ColorGradientSelectComponent } from './components/color-gradient-select/color-gradient-select.component';
import { ProximityFilterSettingsComponent } from './components/filters/proximity-filter-settings/proximity-filter-settings.component';
import { TextEditComponent } from './components/text-edit/text-edit.component';
import { CheckboxComponent } from './components/checkbox/checkbox.component';
import { RelatedAssetsRecipientsSelectionComponent } from './communications/recipients/related-assets-recipients-selection/related-assets-recipients-selection.component';
import { RelatedAssetsRecipientsSelectionListComponent } from './communications/recipients/related-assets-recipients-selection-list/related-assets-recipients-selection-list.component';
import { SeverityBarComponent } from './components/severity-bar/severity-bar.component';
import { BusinessUnitSelectComponent } from './components/business-unit-select/business-unit-select.component';
import { BusinessUnitsDisplayComponent } from './components/business-units-display/business-units-display.component';

import { PhoneInputComponent, ThemeSwitcherComponent } from '@app/shared/components';
import { AddressFieldComponent } from './components/fields/address-field/address-field.component';
import { MapLocationSelectionModule } from '../map-location-selection/map-location-selection.module';
import { SelectMapLocationComponent } from './components/select-location/select-location.component';
import { DurationFieldComponent } from './components/fields/duration-field/duration-field.component';
import { DurationSelectComponent } from './components/duration-select/duration-select.component';
import { EnumerationFieldComponent } from './components/fields/enumeration-field/enumeration-field.component';
import {
  FormatDatePipe,
  FormatLongTextPipe,
  FormatNumberPipe,
  FormatTimezonePipe,
  SafeUrlPipe,
  SafeHtmlPipe,
  StripHtmlPipe
} from '@app/shared/pipes';
import { BusinessUnitsFieldComponent } from './components/fields/business-units-field/business-units-field.component';
import { BusinessUnitsColumnComponent } from './components/columns/business-units-column/business-units-column.component';
import { MatTooltipModule } from '@angular/material/tooltip';

@NgModule({
  declarations: [
    ColorDisplayComponent,
    WorkflowStepDisplayComponent,
    TopoLogoComponent,
    TopoTableComponent,
    TopoTableColumnComponent,
    TopoTableColumnsComponent,
    TopoTablePagerComponent,
    ActionMenuComponent,
    ActionComponent,
    ConfirmDeleteComponent,
    InfiniteScrollerComponent,
    TextColumnComponent,
    DynamicColumnDirective,
    DateTimeColumnComponent,
    MultiPicklistColumnComponent,
    NumberColumnComponent,
    EnumerationColumnComponent,
    BusinessUnitsColumnComponent,
    PicklistColumnComponent,
    UrlColumnComponent,
    ArrayColumnComponent,
    CheckboxComponent,
    RightDrawerComponent,
    WizardDialogComponent,
    RelatedDocumentsComponent,
    RelatedDocumentCardComponent,
    RelatedDocumentsFilterComponent,
    RelatedIncidentCardComponent,
    RelatedAssetPersonCardComponent,
    RelatedAssetFacilityCardComponent,
    IconItemSelectComponent,
    UidColumnComponent,
    UserColumnComponent,
    GeometryColumnComponent,
    ImageColumnComponent,
    TextFieldComponent,
    BusinessUnitsFieldComponent,
    DynamicFieldDirective2,
    DateFieldComponent,
    GeometryFieldComponent,
    AddressFieldComponent,
    ImageFieldComponent,
    PicklistFieldComponent,
    MultiPicklistFieldComponent,
    UidFieldComponent,
    NumberFieldComponent,
    DurationFieldComponent,
    DurationSelectComponent,
    EnumerationFieldComponent,
    EnumerationCellComponent,
    BusinessUnitsCellComponent,
    UrlFieldComponent,
    AutoIncrementFieldComponent,
    ReferenceFieldComponent,
    DocumentSummaryComponent,
    WorkflowSelectComponent,
    FileUploadComponent,
    FileUploadNewComponent,
    SpinnerComponent,
    ProfileMenuComponent,
    ConfirmComponent,
    UserProfileComponent,
    DateTimeCellComponent,
    GeometryCellComponent,
    TextCellComponent,
    UserCellComponent,
    NumberCellComponent,
    UrlCellComponent,
    PicklistCellComponent,
    ReferenceCellComponent,
    MultiPicklistCellComponent,
    ArrayCellComponent,
    UserFieldComponent,
    AppMenuComponent,
    CopyClipboardDirective,
    AppMenuComponent,
    ImageCellComponent,
    WorkflowCellComponent,
    BooleanCellComponent,
    ColorCellComponent,
    BooleanFieldComponent,
    ColorFieldComponent,
    ColorCountPillComponent,
    TabsComponent,
    VerticalTabsComponent,
    DistanceFieldComponent,
    DistanceCellComponent,
    VersionCheckComponent,
    EmailCellComponent,
    PhoneCellComponent,
    DurationCellComponent,
    EnumerationCellComponent,
    CultureCodeCellComponent,
    EmailColumnComponent,
    PhoneColumnComponent,
    CultureCodeColumnComponent,
    CultureCodeFieldComponent,
    ReferenceColumnComponent,
    AutoIncrementColumnComponent,
    EmailFieldComponent,
    PhoneFieldComponent,
    ToolTipComponent,
    HeaderBarTabButtonComponent,
    DocumentFilterDialogComponent,
    ProximityFilterSettingsComponent,
    FilterItemComponent,
    FilterGroupComponent,
    FilterDisplayComponent,
    DocumentLinkFilterDialogComponent,
    LinkFilterItemComponent,
    LinkFilterGroupComponent,
    LinkFilterDisplayComponent,
    LinkFieldSelectModalComponent,
    LinkFilterDatasetItemComponent,
    RecipientSelectComponent,
    RecipientsFromTemplateComponent,
    ContactSelectComponent,
    TemplateBuilderComponent,
    RichTextEditComponent,
    TextEditComponent,
    FieldSelectModalComponent,
    LinkCellComponent,
    ActionCellComponent,
    TooltipDirective,
    TooltipComponent,
    TableColumnSelection,
    DataTableComponent,
    DataTableServerComponent,
    EnumTranslateCellComponent,
    VideoWindowComponent,
    OpenSourceLicensesComponent,
    ArrayFieldComponent,
    TextInputAutocompleteDirective,
    TextInputAutocompleteContainerComponent,
    TextInputAutocompleteMenuComponent,
    TableFilterComponent,
    RecipientInlineSelectComponent,
    RecipientListSelectComponent,
    RecipientsSelectionComponent,
    RecipientDetailsListComponent,
    NotificationLogComponent,
    NotificationLogStatusDetailComponent,
    FormGroupSelectComponent,
    ColorGradientSelectComponent,
    RelatedAssetsRecipientsSelectionComponent,
    RelatedAssetsRecipientsSelectionListComponent,
    SeverityBarComponent,
    TagPillComponent,
    BusinessUnitSelectComponent,
    BusinessUnitsDisplayComponent,
    SelectMapLocationComponent
  ],
  imports: [
    CommonModule,
    StoreModule.forFeature('core', store.reducers),
    EffectsModule.forFeature([RoutingEffects, UserProfileEffects]),
    FlexLayoutModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule.forChild({
      extend: true
    }),
    RouterModule,
    ScrollingModule,
    DragDropModule,
    TextFieldModule,
    MatListModule,
    MatTreeModule,
    MatCheckboxModule,
    MatSliderModule,
    MatTooltipModule,
    MatDatepickerModule,
    MatMomentDateModule,
    NgxMaterialTimepickerModule,
    NgxDropzoneModule,
    NgSelectModule,
    AgGridModule,
    NgxSpinnerModule,
    ImageCropperModule,
    ColorPickerModule,
    NgxSkeletonLoaderModule,
    QuillModule.forRoot(),
    ThemeSwitcherComponent,
    DateTimePickerComponent,
    FormatDatePipe,
    MapLocationSelectionModule,
    SwitchComponent,
    ColorPickerComponent,
    SliderComponent,
    VideoPlayerComponent,
    AvatarComponent,
    WorkflowTransitionSelectComponent2,
    ColorItemSelectComponent,
    WorkflowTransitionFormComponent,
    ModalComponent,
    DynamicFieldDirective,
    SafeUrlPipe,
    SafeHtmlPipe,
    StripHtmlPipe,
    FormatNumberPipe,
    FormatLongTextPipe,
    FormatTimezonePipe,
    PhoneInputComponent
  ],
  exports: [
    ColorDisplayComponent,
    WorkflowStepDisplayComponent,
    TopoLogoComponent,
    TopoTableComponent,
    TopoTableColumnComponent,
    TopoTableColumnsComponent,
    TopoTablePagerComponent,
    ActionMenuComponent,
    ActionComponent,
    ModalComponent,
    ConfirmDeleteComponent,
    InfiniteScrollerComponent,
    TextColumnComponent,
    DynamicColumnDirective,
    DateTimeColumnComponent,
    MultiPicklistColumnComponent,
    NumberColumnComponent,
    PicklistColumnComponent,
    EnumerationColumnComponent,
    BusinessUnitsColumnComponent,
    UrlColumnComponent,
    ArrayColumnComponent,
    ColorItemSelectComponent,
    CheckboxComponent,
    RightDrawerComponent,
    WizardDialogComponent,
    WorkflowTransitionFormComponent,
    RelatedDocumentsComponent,
    RelatedDocumentCardComponent,
    RelatedDocumentsFilterComponent,
    RelatedIncidentCardComponent,
    RelatedAssetPersonCardComponent,
    RelatedAssetFacilityCardComponent,
    IconItemSelectComponent,
    UidColumnComponent,
    UserColumnComponent,
    GeometryColumnComponent,
    ImageColumnComponent,
    DynamicFieldDirective,
    DynamicFieldDirective2,
    DocumentSummaryComponent,
    WorkflowTransitionSelectComponent2,
    WorkflowSelectComponent,
    AvatarComponent,
    FileUploadComponent,
    FileUploadNewComponent,
    NgSelectModule,
    SpinnerComponent,
    MatListModule,
    MatTreeModule,
    MatCheckboxModule,
    MatSliderModule,
    SliderComponent,
    ProfileMenuComponent,
    ConfirmComponent,
    UserProfileComponent,
    DateTimeCellComponent,
    GeometryCellComponent,
    TextCellComponent,
    UserCellComponent,
    NumberCellComponent,
    UrlCellComponent,
    PicklistCellComponent,
    ReferenceCellComponent,
    MultiPicklistCellComponent,
    ArrayCellComponent,
    AppMenuComponent,
    CopyClipboardDirective,
    UserFieldComponent,
    ImageCellComponent,
    WorkflowCellComponent,
    ColorPickerComponent,
    BooleanCellComponent,
    ColorCellComponent,
    BooleanFieldComponent,
    ColorFieldComponent,
    ColorCountPillComponent,
    SwitchComponent,
    TabsComponent,
    VerticalTabsComponent,
    VersionCheckComponent,
    EmailCellComponent,
    PhoneCellComponent,
    DurationCellComponent,
    EnumerationCellComponent,
    BusinessUnitsCellComponent,
    DurationSelectComponent,
    CultureCodeCellComponent,
    EmailColumnComponent,
    PhoneColumnComponent,
    CultureCodeColumnComponent,
    CultureCodeFieldComponent,
    ReferenceColumnComponent,
    AutoIncrementColumnComponent,
    EmailFieldComponent,
    PhoneFieldComponent,
    ToolTipComponent,
    VideoPlayerComponent,
    HeaderBarTabButtonComponent,
    DocumentFilterDialogComponent,
    ProximityFilterSettingsComponent,
    FilterItemComponent,
    FilterGroupComponent,
    FilterDisplayComponent,
    RecipientSelectComponent,
    RecipientsFromTemplateComponent,
    ContactSelectComponent,
    TemplateBuilderComponent,
    RichTextEditComponent,
    TextEditComponent,
    LinkCellComponent,
    NgxSkeletonLoaderModule,
    TooltipDirective,
    TableColumnSelection,
    DataTableComponent,
    DateTimePickerComponent,
    DataTableComponent,
    DataTableServerComponent,
    VideoWindowComponent,
    QuillModule,
    ColorPickerModule,
    TextInputAutocompleteDirective,
    TextInputAutocompleteContainerComponent,
    TextInputAutocompleteMenuComponent,
    TableFilterComponent,
    RecipientInlineSelectComponent,
    RecipientListSelectComponent,
    RecipientsSelectionComponent,
    RecipientDetailsListComponent,
    DocumentLinkFilterDialogComponent,
    LinkFilterItemComponent,
    LinkFilterGroupComponent,
    LinkFilterDisplayComponent,
    LinkFieldSelectModalComponent,
    LinkFilterDatasetItemComponent,
    NotificationLogComponent,
    NotificationLogStatusDetailComponent,
    FormGroupSelectComponent,
    ColorGradientSelectComponent,
    RelatedAssetsRecipientsSelectionComponent,
    SeverityBarComponent,
    TagPillComponent,
    BusinessUnitSelectComponent,
    BusinessUnitsDisplayComponent,
    SelectMapLocationComponent
  ]
})
export class CoreModule {}
